import React, { useEffect, useRef, useState } from "react";

const START_YEAR = 2026;
const END_YEAR = 1995;
const STEP_MS = 155;          // year tick speed
const WELCOME_MS = 2600;
const FADE_MS = 900;

const STREAKS = Array.from({ length: 46 }).map((_, i) => ({
  a: Math.round((360 / 46) * i + Math.random() * 6),
  delay: (Math.random() * 1.9).toFixed(2),
  dur: (1.4 + Math.random() * 1.0).toFixed(2),
  w: (1 + Math.random() * 2).toFixed(1),
}));

// starfield behind the globe
const STARS = Array.from({ length: 70 }).map(() => ({
  x: (Math.random() * 100).toFixed(1),
  y: (Math.random() * 100).toFixed(1),
  s: (Math.random() * 2 + 1).toFixed(1),
  delay: (Math.random() * 2.6).toFixed(2),
}));

// dreamy time-travel score (warm pad + shimmer arpeggio + faint whoosh)
function playTimeTravelMusic() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC();
    const t0 = ctx.currentTime;
    const DUR = 9.6;
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.exponentialRampToValueAtTime(0.5, t0 + 1.0);
    master.gain.setValueAtTime(0.5, t0 + DUR - 1.3);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + DUR);
    const delay = ctx.createDelay();
    delay.delayTime.value = 0.28;
    const fb = ctx.createGain();
    fb.gain.value = 0.34;
    delay.connect(fb).connect(delay);
    master.connect(delay);
    master.connect(ctx.destination);
    delay.connect(ctx.destination);

    [110, 164.81, 220, 261.63].forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = i < 2 ? "sine" : "triangle";
      o.frequency.value = f;
      o.detune.value = (i - 1.5) * 4;
      const g = ctx.createGain();
      g.gain.value = 0.12 - i * 0.015;
      o.connect(g).connect(master);
      o.start(t0); o.stop(t0 + DUR);
    });

    const scale = [440, 523.25, 587.33, 659.25, 783.99, 880, 1046.5];
    let step = 0;
    for (let t = 0.6; t < DUR - 1.3; t += 0.34) {
      const f = scale[step % scale.length] * (step > 12 ? 2 : 1);
      step += 1;
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      const g = ctx.createGain();
      const at = t0 + t;
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.16, at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.6);
      o.connect(g).connect(master);
      o.start(at); o.stop(at + 0.65);
    }

    const buf = ctx.createBuffer(1, ctx.sampleRate * DUR, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(500, t0);
    lp.frequency.exponentialRampToValueAtTime(1400, t0 + DUR * 0.6);
    lp.frequency.exponentialRampToValueAtTime(300, t0 + DUR);
    const nGain = ctx.createGain();
    nGain.gain.value = 0.10;
    noise.connect(lp).connect(nGain).connect(master);
    noise.start(t0); noise.stop(t0 + DUR);

    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    return ctx;
  } catch {
    return null;
  }
}

export const TimeTravelIntro = ({ onDone }) => {
  const [year, setYear] = useState(START_YEAR);
  const [phase, setPhase] = useState("rewind"); // rewind | india | welcome
  const [zoom, setZoom] = useState(1);
  const [fading, setFading] = useState(false);
  const [earthReady, setEarthReady] = useState(false);
  const finished = useRef(false);
  const ctxRef = useRef(null);
  const timers = useRef([]);

  const finish = () => {
    if (finished.current) return;
    finished.current = true;
    setFading(true);
    try { ctxRef.current && ctxRef.current.close(); } catch { /* noop */ }
    timers.current.push(setTimeout(() => onDone && onDone(), FADE_MS));
  };

  // Preload + DECODE the Earth texture so the globe paints on its first frame (no empty circle / delay).
  useEffect(() => {
    let cancelled = false;
    const start = () => { if (!cancelled) setEarthReady(true); };
    const img = new Image();
    img.src = "/intro/earth.webp";
    (img.decode ? img.decode() : Promise.reject()).then(start).catch(() => {
      img.onload = start; img.onerror = start; if (img.complete) start();
    });
    const fb = setTimeout(start, 2000); // safety net
    return () => { cancelled = true; clearTimeout(fb); };
  }, []);

  useEffect(() => {
    if (!earthReady) return;
    ctxRef.current = playTimeTravelMusic();
    const resume = () => { try { const c = ctxRef.current; if (c && c.state === "suspended") c.resume().catch(() => {}); } catch { /* noop */ } };
    window.addEventListener("pointerdown", resume, { once: true });

    // slowly zoom the globe during the rewind ("zoom zoom zoom")
    timers.current.push(setTimeout(() => setZoom(2.2), 120));

    let y = START_YEAR;
    const tick = setInterval(() => {
      y -= 1;
      if (y <= END_YEAR) {
        setYear(END_YEAR);
        clearInterval(tick);
        setPhase("welcome");
        timers.current.push(setTimeout(finish, WELCOME_MS));
      } else {
        setYear(y);
      }
    }, STEP_MS);
    timers.current.push(tick);

    return () => { timers.current.forEach((t) => { clearTimeout(t); clearInterval(t); }); window.removeEventListener("pointerdown", resume); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earthReady]);

  const welcoming = phase === "welcome";

  return (
    <div
      data-testid="time-travel-intro"
      className={`fixed inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden ${fading ? "tt-fade-out" : ""}`}
      style={{ background: "radial-gradient(circle at 50% 45%, #0c1830 0%, #060b18 55%, #02040a 100%)" }}
    >
      {/* starfield */}
      <div className="pointer-events-none absolute inset-0">
        {STARS.map((st, i) => (
          <span key={i} className="tt-star absolute rounded-full bg-white"
            style={{ left: `${st.x}%`, top: `${st.y}%`, width: `${st.s}px`, height: `${st.s}px`, animationDelay: `${st.delay}s` }} />
        ))}
      </div>

      {/* warp streaks (fade once we arrive) */}
      <div className={`pointer-events-none absolute left-1/2 top-1/2 h-0 w-0 transition-opacity duration-700 ${welcoming ? "opacity-30" : "opacity-100"}`}>
        {STREAKS.map((s, i) => (
          <span key={i} className="tt-warp absolute block rounded-full bg-gradient-to-t from-transparent via-sky-200/70 to-white"
            style={{ "--a": `${s.a}deg`, width: `${s.w}px`, height: "22vmax", animationDelay: `${s.delay}s`, animationDuration: `${s.dur}s` }} />
        ))}
      </div>

      {/* ---- SPINNING GLOBE (always mounted so the bg decodes early; revealed instantly once ready) ---- */}
      <div className="relative" style={{ width: 300, height: 300, transform: `scale(${zoom})`, transition: "transform 4.8s cubic-bezier(0.35,0,0.4,1)", opacity: earthReady ? 1 : 0 }}>
        {/* rotating surface — CSS background-position spin (reliable on mobile); revealed instantly once decoded */}
        <div className="tt-globe h-full w-full rounded-full"
          style={{ backgroundImage: "url(/intro/earth.webp)", backgroundSize: "auto 300px", backgroundRepeat: "repeat-x", filter: "saturate(1.08) brightness(1.02)" }} />
        {/* pulsing atmosphere glow (separate element so it doesn't override the spin) */}
        <div className="tt-glow pointer-events-none absolute inset-0 rounded-full" />
        <div className="pointer-events-none absolute inset-0 rounded-full" style={{ boxShadow: "inset 16px 12px 34px rgba(255,255,255,0.18)" }} />
        <div className="pointer-events-none absolute -inset-3 rounded-full" style={{ background: "radial-gradient(circle, rgba(120,190,255,0.18) 60%, transparent 72%)" }} />
      </div>

      {/* caption */}
      <div className="pointer-events-none absolute inset-x-0 bottom-[15%] px-4 text-center">
        {phase === "rewind" ? (
          <>
            <p className="font-signboard text-sm uppercase tracking-[0.35em] text-sky-300/90">Rewinding time…</p>
            <div key={year} className="tt-year-pop font-pixel text-4xl font-bold text-sky-200 drop-shadow-[0_0_18px_rgba(120,190,255,0.9)] sm:text-5xl" data-testid="tt-year">{year}</div>
          </>
        ) : (
          <div className="tt-welcome-in" data-testid="tt-welcome">
            <div className="font-signboard text-5xl font-bold text-kirana-cream drop-shadow-[0_0_22px_rgba(240,200,120,0.75)] sm:text-6xl">Welcome to the Past</div>
            <p className="mt-3 text-base text-sky-200/85 sm:text-lg">Hope you get to remember yourself ✨</p>
          </div>
        )}
      </div>

      <button data-testid="tt-skip" onClick={finish}
        className="absolute bottom-6 right-6 rounded-full border-2 border-white/30 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white/90 backdrop-blur-sm transition-colors hover:bg-white/20">
        Skip ▸
      </button>
    </div>
  );
};
